// The inputs of a Tool that runs on the composer (#209): live's slots follow the tool's block
// (its edit collection), with no narrowing, since the tool, not the inputs, picks the type.
import type { GenerationType } from "../../../api/generated/model";
import type { CatalogModel } from "../draft";
import type { InputState, VideoInput } from "../video/modes";

/** The tool's block on this model, or undefined where the model has none. */
function blockOf(model: CatalogModel, type: GenerationType) {
  return model.medium === "video" ? model.modes[type] : undefined;
}

/**
 * Whether the tool's block takes an input: the frames and the shots never (live's edit slots
 * are the elements, the image refs and the video ref), the others where the block has room.
 */
export function toolInputState(
  model: CatalogModel,
  type: GenerationType,
  input: VideoInput,
): InputState {
  const block = blockOf(model, type);
  if (block === undefined) return "hidden";
  switch (input) {
    case "references":
      return (block.reference_images?.max ?? 0) > 0 ? "enabled" : "hidden";
    case "elements":
      return (block.elements?.max ?? 0) > 0 ? "enabled" : "hidden";
    // Its own video is its slot (`sourceVideo`); reference videos beside it only where the block
    // takes them (Kling 4.0 Edit's, spec web/edit-reference-videos), and no audios.
    case "videos":
      return (block.reference_videos?.max ?? 0) > 0 ? "enabled" : "hidden";
    case "audios":
    case "voices":
    case "start":
    case "end":
    case "middle":
    case "shots":
      return "hidden";
  }
}

/** The reference images the tool's block takes on this model (live's `maxReferenceImages`). */
export function toolReferencesMax(model: CatalogModel, type: GenerationType): number {
  return blockOf(model, type)?.reference_images?.max ?? 0;
}

/** The reference videos the tool's block takes on this model, beside its own video. */
export function toolVideosMax(model: CatalogModel, type: GenerationType): number {
  return blockOf(model, type)?.reference_videos?.max ?? 0;
}
